/**
 * LA FORMULE D'UN CHAMP `calcul` : un langage FERME, un seul evaluateur
 * (`docs/socle/champs-types.md`, section 3).
 *
 * POURQUOI CE N'EST PAS DU CODE. La ligne rouge de `taki.fields.json` (« aucune
 * expression ») cede ici, et seulement ici, sur decision du fondateur. Ce qui la
 * rend tenable : la formule n'est jamais executee, elle est LUE. Un analyseur a
 * descente recursive la decoupe en jetons connus d'avance, puis en arbre ; rien
 * ne passe par `eval`, `Function` ni une expression reguliere construite a
 * partir de la saisie. Un mot qui n'est ni un nombre, ni un champ du jeu, ni
 * l'une des quatre fonctions est refuse : `constructor`, `__proto__` ou
 * `process.exit()` ne designent rien ici, ils sont des fautes.
 *
 * CE QU'ELLE SAIT, et rien d'autre : nombres (`0,25` ou `0.25`), champs de LA
 * fiche, `+ - * / ^`, parentheses, moins unaire, `MIN`, `MAX`, `ABS`,
 * `ARRONDI(x; n)`. Le separateur d'arguments est `;`, comme un tableur
 * francais : la virgule y est deja la marque decimale.
 *
 * UN NATIF NUMERIQUE DE LA FAMILLE se cite par `{socle:<natif>}` (`natifsCitables`,
 * `docs/socle/natifs-taux-calcul.md`) : le prix HT `{socle:basePrice}` et le
 * taux `{socle:taxRate}` d'un produit entrent dans un TTC sans doublon.
 *
 * DEUX ECRITURES D'UNE MEME FORMULE :
 * - la SAISIE, celle que l'on tape : un champ s'y nomme par son libelle (`Prix`),
 *   entre crochets quand il a des espaces (`[Prime annuelle]`), ou par sa clef
 *   entre accolades (`{prix}`), sans egard pour la casse ni les accents ;
 * - la forme STOCKEE, canonique : chaque champ par sa clef (`{prix}`), chaque
 *   nombre avec un point. Renommer un champ ne casse donc rien.
 * `canoniserFormule` passe de l'une a l'autre, `formuleLisible` revient.
 *
 * PRIORITES, celles de l'ecole : `^` d'abord (associatif a droite), puis le
 * moins unaire, puis `*` et `/`, puis `+` et `-`. `-2 ^ 2` vaut donc -4 et
 * `2 ^ -1` vaut 0,5.
 *
 * UNE VALEUR QUI NE SE CALCULE PAS EST VIDE, jamais zero : un champ cite vide,
 * une division par zero ou un resultat infini rendent `null`. Zero serait une
 * valeur fausse qui s'additionne en silence dans un total.
 *
 * PARTAGE TEL QUEL par le client (`@taki/data-model`, l'ecran recalcule dans le
 * patch qu'il ecrit) et par le serveur (embarque dans le bundle des Functions,
 * le declencheur des fiches corrige apres une ecriture MCP, Lia, flux ou
 * import) : aucune dependance hors du data-model.
 */

import { estNumerique, ficheDuType } from "./catalogue-des-types.mjs";
import { champsNatifs, estFamille, PREFIXE_CHAMP_SOCLE } from "./familles.js";

/** Un champ, reduit a ce qu'une formule en lit. */
export interface ChampDeFormule {
  key: string;
  label?: string | undefined;
  type: string;
  formule?: string | undefined;
  decimales?: number | undefined;
  devise?: string | undefined;
}

/** Les quatre fonctions du langage. */
export type FonctionDeFormule = "MIN" | "MAX" | "ABS" | "ARRONDI";
export const FONCTIONS_DE_FORMULE: readonly FonctionDeFormule[] = Object.freeze(["MIN", "MAX", "ABS", "ARRONDI"]);

/** Une formule analysee. `position` : l'index (a partir de 0) du jeton dans le texte. */
export type NoeudFormule =
  | { sorte: "nombre"; valeur: number; position: number }
  | { sorte: "champ"; nom: string; position: number }
  | { sorte: "unaire"; operateur: "+" | "-"; operande: NoeudFormule; position: number }
  | { sorte: "binaire"; operateur: "+" | "-" | "*" | "/" | "^"; gauche: NoeudFormule; droite: NoeudFormule; position: number }
  | { sorte: "fonction"; nom: FonctionDeFormule; arguments: NoeudFormule[]; position: number };

/** Une faute de lecture, et ou elle est (index a partir de 0). */
export interface ErreurDeFormule {
  erreur: string;
  position: number;
}

/*
 * DES BORNES, parce qu'une formule vient d'une saisie : un texte de 10 000
 * parentheses ferait deborder la pile de l'analyseur. Aucune formule metier ne
 * les approche.
 */
export const LONGUEUR_MAX_FORMULE = 500;
const PROFONDEUR_MAX = 40;

/* -------------------------------------------------------------------------- */
/* Les jetons                                                                 */
/* -------------------------------------------------------------------------- */

type Operateur = "+" | "-" | "*" | "/" | "^";
type Jeton =
  | { sorte: "nombre"; texte: string; valeur: number; position: number }
  | { sorte: "champ"; nom: string; position: number }
  | { sorte: "fonction"; nom: FonctionDeFormule; position: number }
  | { sorte: "operateur"; operateur: Operateur; position: number }
  | { sorte: "ouvrante" | "fermante" | "separateur"; position: number };

/*
 * Les classes de caracteres sont des expressions LITTERALES, ecrites une fois :
 * aucune n'est construite a partir de la saisie.
 */
const DEBUT_DE_MOT = /[\p{L}_]/u;
const SUITE_DE_MOT = /[\p{L}\p{N}_]/u;
const MOT_NU = /^[\p{L}_][\p{L}\p{N}_]*$/u;
const ESPACE = /\s/u;
const estChiffre = (c: string | undefined): boolean => c !== undefined && c >= "0" && c <= "9";

/** Un mot sans casse ni accents ni espaces doubles : la cle de toute comparaison de noms. */
function normaliser(mot: string): string {
  return mot.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

function fonctionNommee(mot: string): FonctionDeFormule | undefined {
  const cherche = normaliser(mot).toUpperCase();
  return FONCTIONS_DE_FORMULE.find((nom) => nom === cherche);
}

class FauteDeLecture extends Error {
  constructor(message: string, readonly position: number) {
    super(message);
  }
}

const OPERATEURS: readonly string[] = ["+", "-", "*", "/", "^"];

function decouper(texte: string): Jeton[] {
  if (texte.length > LONGUEUR_MAX_FORMULE) {
    throw new FauteDeLecture(`une formule tient en ${LONGUEUR_MAX_FORMULE} caractères au plus`, LONGUEUR_MAX_FORMULE);
  }
  const jetons: Jeton[] = [];
  let i = 0;
  while (i < texte.length) {
    const c = texte[i]!;
    if (ESPACE.test(c)) { i += 1; continue; }
    const debut = i;
    if (estChiffre(c)) {
      while (estChiffre(texte[i])) i += 1;
      if (texte[i] === "," || texte[i] === ".") {
        if (!estChiffre(texte[i + 1])) throw new FauteDeLecture(`un nombre ne finit pas par "${texte[i]}" (le séparateur des arguments est ;)`, i);
        i += 1;
        while (estChiffre(texte[i])) i += 1;
      }
      const brut = texte.slice(debut, i);
      jetons.push({ sorte: "nombre", texte: brut, valeur: Number(brut.replace(",", ".")), position: debut });
      continue;
    }
    if (c === "{" || c === "[") {
      const fin = c === "{" ? "}" : "]";
      const ferme = texte.indexOf(fin, i + 1);
      if (ferme < 0) throw new FauteDeLecture(`"${c}" ouvert sans "${fin}"`, i);
      const nom = texte.slice(i + 1, ferme).trim();
      if (nom.length === 0) throw new FauteDeLecture(`"${c}${fin}" ne nomme aucun champ`, i);
      if (nom.includes(c)) throw new FauteDeLecture(`"${c}" ouvert deux fois`, i);
      jetons.push({ sorte: "champ", nom, position: debut });
      i = ferme + 1;
      continue;
    }
    if (DEBUT_DE_MOT.test(c)) {
      while (i < texte.length && SUITE_DE_MOT.test(texte[i]!)) i += 1;
      const mot = texte.slice(debut, i);
      let apres = i;
      while (apres < texte.length && ESPACE.test(texte[apres]!)) apres += 1;
      if (texte[apres] === "(") {
        const nom = fonctionNommee(mot);
        if (nom === undefined) throw new FauteDeLecture(`"${mot}" n'est pas une fonction (les fonctions sont ${FONCTIONS_DE_FORMULE.join(", ")})`, debut);
        jetons.push({ sorte: "fonction", nom, position: debut });
      } else {
        jetons.push({ sorte: "champ", nom: mot, position: debut });
      }
      continue;
    }
    if (OPERATEURS.includes(c)) {
      jetons.push({ sorte: "operateur", operateur: c as Operateur, position: debut });
    } else if (c === "(") {
      jetons.push({ sorte: "ouvrante", position: debut });
    } else if (c === ")") {
      jetons.push({ sorte: "fermante", position: debut });
    } else if (c === ";") {
      jetons.push({ sorte: "separateur", position: debut });
    } else {
      throw new FauteDeLecture(`caractère inattendu "${c}"`, debut);
    }
    i += 1;
  }
  return jetons;
}

/* -------------------------------------------------------------------------- */
/* L'arbre                                                                    */
/* -------------------------------------------------------------------------- */

const ARITES: Record<FonctionDeFormule, { min: number; max: number }> = {
  MIN: { min: 1, max: Infinity },
  MAX: { min: 1, max: Infinity },
  ABS: { min: 1, max: 1 },
  ARRONDI: { min: 1, max: 2 },
};

function construire(jetons: readonly Jeton[], longueur: number): NoeudFormule {
  let i = 0;
  const courant = (): Jeton | undefined => jetons[i];
  const positionCourante = (): number => courant()?.position ?? longueur;
  const estOperateur = (j: Jeton | undefined, ...ops: Operateur[]): j is Extract<Jeton, { sorte: "operateur" }> =>
    j !== undefined && j.sorte === "operateur" && ops.includes(j.operateur);

  const expression = (profondeur: number): NoeudFormule => {
    if (profondeur > PROFONDEUR_MAX) throw new FauteDeLecture("formule trop imbriquée", positionCourante());
    let gauche = terme(profondeur);
    for (let j = courant(); estOperateur(j, "+", "-"); j = courant()) {
      i += 1;
      gauche = { sorte: "binaire", operateur: j.operateur, gauche, droite: terme(profondeur), position: j.position };
    }
    return gauche;
  };
  const terme = (profondeur: number): NoeudFormule => {
    let gauche = unaire(profondeur);
    for (let j = courant(); estOperateur(j, "*", "/"); j = courant()) {
      i += 1;
      gauche = { sorte: "binaire", operateur: j.operateur, gauche, droite: unaire(profondeur), position: j.position };
    }
    return gauche;
  };
  const unaire = (profondeur: number): NoeudFormule => {
    if (profondeur > PROFONDEUR_MAX) throw new FauteDeLecture("formule trop imbriquée", positionCourante());
    const j = courant();
    if (estOperateur(j, "+", "-")) {
      i += 1;
      return { sorte: "unaire", operateur: j.operateur === "-" ? "-" : "+", operande: unaire(profondeur + 1), position: j.position };
    }
    return puissance(profondeur);
  };
  const puissance = (profondeur: number): NoeudFormule => {
    const base = primaire(profondeur);
    const j = courant();
    if (estOperateur(j, "^")) {
      i += 1;
      return { sorte: "binaire", operateur: "^", gauche: base, droite: unaire(profondeur + 1), position: j.position };
    }
    return base;
  };
  const primaire = (profondeur: number): NoeudFormule => {
    const j = courant();
    if (j === undefined) throw new FauteDeLecture("la formule s'arrête avant la fin : il manque une valeur", longueur);
    if (j.sorte === "nombre") { i += 1; return { sorte: "nombre", valeur: j.valeur, position: j.position }; }
    if (j.sorte === "champ") { i += 1; return { sorte: "champ", nom: j.nom, position: j.position }; }
    if (j.sorte === "ouvrante") {
      i += 1;
      const dedans = expression(profondeur + 1);
      if (courant()?.sorte !== "fermante") throw new FauteDeLecture('il manque une ")"', positionCourante());
      i += 1;
      return dedans;
    }
    if (j.sorte === "fonction") {
      i += 1;
      if (courant()?.sorte !== "ouvrante") throw new FauteDeLecture(`"${j.nom}" s'écrit avec ses parenthèses`, positionCourante());
      i += 1;
      const args: NoeudFormule[] = [expression(profondeur + 1)];
      while (courant()?.sorte === "separateur") {
        i += 1;
        args.push(expression(profondeur + 1));
      }
      if (courant()?.sorte !== "fermante") throw new FauteDeLecture(`il manque une ")" pour fermer ${j.nom} (les arguments se séparent par ;)`, positionCourante());
      i += 1;
      const arite = ARITES[j.nom];
      if (args.length < arite.min || args.length > arite.max) {
        const attendu = arite.max === Infinity ? `au moins ${arite.min} argument` : arite.min === arite.max ? `${arite.min} argument` : `${arite.min} ou ${arite.max} arguments`;
        throw new FauteDeLecture(`${j.nom} prend ${attendu}`, j.position);
      }
      return { sorte: "fonction", nom: j.nom, arguments: args, position: j.position };
    }
    if (j.sorte === "fermante") throw new FauteDeLecture('")" sans "(" ouverte, ou valeur manquante avant', j.position);
    if (j.sorte === "separateur") throw new FauteDeLecture('";" sépare les arguments d\'une fonction, et seulement eux', j.position);
    if (j.sorte === "operateur") throw new FauteDeLecture(`"${j.operateur}" sans valeur avant`, j.position);
    throw new FauteDeLecture("valeur attendue", j.position);
  };

  const arbre = expression(0);
  const reste = courant();
  if (reste !== undefined) {
    const message = reste.sorte === "fermante"
      ? '")" sans "(" ouverte'
      : reste.sorte === "separateur"
        ? '";" sépare les arguments d\'une fonction, et seulement eux'
        : "il manque un opérateur (+ - * / ^) entre deux valeurs";
    throw new FauteDeLecture(message, reste.position);
  }
  return arbre;
}

function lire(texte: string): { jetons: Jeton[]; arbre: NoeudFormule } | ErreurDeFormule {
  try {
    const jetons = decouper(texte);
    if (jetons.length === 0) return { erreur: "la formule est vide", position: 0 };
    return { jetons, arbre: construire(jetons, texte.length) };
  } catch (faute) {
    if (faute instanceof FauteDeLecture) return { erreur: faute.message, position: faute.position };
    throw faute;
  }
}

/** Lire une formule (saisie ou stockee) : son arbre, ou la faute et sa position. */
export function analyserFormule(texte: string): { arbre: NoeudFormule } | ErreurDeFormule {
  const lu = lire(texte);
  return "erreur" in lu ? lu : { arbre: lu.arbre };
}

/** La faute de lecture en une phrase, avec sa position comptee a partir de 1. */
const phraseDeFaute = (faute: ErreurDeFormule): string => `${faute.erreur} (caractère ${faute.position + 1})`;

/* -------------------------------------------------------------------------- */
/* Les champs cites                                                           */
/* -------------------------------------------------------------------------- */

type Resolution<C> = { champ: C } | { inconnu: string } | { ambigus: C[] };

/**
 * LE CHAMP QU'UN NOM DESIGNE, dans cet ordre : la clef exacte, puis une clef ou
 * un libelle egal sans casse ni accents. Deux candidats au meme rang : le nom
 * est ambigu, et on le dit plutot que de choisir.
 */
function resoudre<C extends ChampDeFormule>(nom: string, champs: readonly C[]): Resolution<C> {
  const exact = champs.find((champ) => champ.key === nom);
  if (exact !== undefined) return { champ: exact };
  const cherche = normaliser(nom);
  for (const lecture of [(champ: C) => champ.key, (champ: C) => champ.label ?? ""]) {
    const trouves = champs.filter((champ) => normaliser(lecture(champ)) === cherche);
    if (trouves.length === 1) return { champ: trouves[0]! };
    if (trouves.length > 1) return { ambigus: trouves };
  }
  return { inconnu: nom };
}

/** Pourquoi un nom ne designe pas UN champ, en une phrase qui commence par le nom. */
function phraseDeResolution(nom: string, resolu: { inconnu: string } | { ambigus: readonly ChampDeFormule[] }): string {
  if ("inconnu" in resolu && nom.startsWith(PREFIXE_CHAMP_SOCLE)) return `« ${nom} », qui n'est pas un champ natif numérique de la famille de ce jeu`;
  if ("inconnu" in resolu) return `« ${nom} », qui n'est pas un champ de ce jeu`;
  return `« ${nom} », que portent plusieurs champs : nomme-le par sa clef (${resolu.ambigus.map((champ) => `{${champ.key}}`).join(" ou ")})`;
}

const nomDuChamp = (champ: ChampDeFormule): string => `« ${champ.label ?? champ.key} »`;

/**
 * LES CHAMPS QU'UNE FORMULE CITE, une fois chacun, dans l'ordre de lecture.
 * Avec les champs du jeu, chaque nom resolu devient sa clef ; sans eux (ou pour
 * un nom qui ne se resout pas), le nom tel qu'ecrit. Une formule illisible ne
 * cite rien.
 */
export function champsCites(formule: string, champs?: readonly ChampDeFormule[]): string[] {
  const lu = lire(formule);
  if ("erreur" in lu) return [];
  const noms: string[] = [];
  for (const jeton of lu.jetons) {
    if (jeton.sorte !== "champ") continue;
    const resolu = champs === undefined ? undefined : resoudre(jeton.nom, champs);
    const nom = resolu !== undefined && "champ" in resolu ? resolu.champ.key : jeton.nom;
    if (!noms.includes(nom)) noms.push(nom);
  }
  return noms;
}

/* -------------------------------------------------------------------------- */
/* Les champs natifs citables                                                  */
/* -------------------------------------------------------------------------- */

/**
 * UN NATIF NUMERIQUE SE CITE PAR `{socle:<natif>}`, la convention des colonnes de
 * `taki.screens.json` (`socle:basePrice`). Le natif vit a la RACINE de la fiche
 * (`basePrice`), jamais dans le sac `customFields` : un prix HT du socle et un
 * taux de taxe du socle entrent ainsi dans un prix TTC, sans doublon dans le
 * sac. Il vaut sa valeur stockee, comme tout nombre d'une formule.
 *
 * Il se presente a la formule comme un champ de plus du jeu : la clef
 * `socle:basePrice`, le type `number`, un libelle. Tout le reste (resolution
 * d'un nom, controle de nature, evaluation) est l'unique chemin des champs
 * declares. Seuls comptent les natifs de GENRE nombre de la famille du jeu
 * (`champsNatifs`) ; un natif texte, date, enum ou une trace n'en est pas un.
 */
export function estCitationNative(clef: string): boolean {
  return clef.startsWith(PREFIXE_CHAMP_SOCLE);
}

/**
 * LES NATIFS NUMERIQUES D'UNE FAMILLE, tels qu'une formule les cite. `libelles`
 * donne le mot a montrer, par clef complete (`socle:basePrice`) : c'est le
 * libelle de la colonne native de l'ecran (« Prix HT »). SANS LIBELLE, le natif
 * ne se cite que par sa clef : un mot comme `basePrice` ne doit pas se resoudre
 * ici et pas ailleurs, selon qui connait le libelle. Une famille inconnue n'en
 * a aucun (le jeu "a decider" d'une app neuve).
 */
export function natifsCitables(famille: string, libelles: Readonly<Record<string, string>> = {}): ChampDeFormule[] {
  if (!estFamille(famille)) return [];
  return champsNatifs(famille)
    .filter((natif) => natif.genre === "number")
    .map((natif) => {
      const key = `${PREFIXE_CHAMP_SOCLE}${natif.key}`;
      const label = libelles[key];
      return label === undefined ? { key, type: "number" } : { key, label, type: "number" };
    });
}

/**
 * LES CHAMPS QU'UNE FORMULE PEUT CITER dans un jeu : ses champs declares, puis
 * les natifs numeriques de sa famille (`natifsCitables`). C'est la liste que
 * lisent `canoniserFormule`, `verifierFormule`, `recalculer` et le Studio.
 * Un ecran qui montre plusieurs jeux passe leurs familles : un natif commun
 * n'y figure qu'une fois.
 */
export function champsAvecNatifs<C extends ChampDeFormule>(
  famille: string | readonly string[],
  champs: readonly C[],
  libelles?: Readonly<Record<string, string>>,
): (C | ChampDeFormule)[] {
  const sortie: (C | ChampDeFormule)[] = [...champs];
  /* Une famille absente (un depot de test, un jeu sans famille) n'a aucun natif. */
  for (const nom of typeof famille === "string" ? [famille] : (famille ?? [])) {
    for (const natif of natifsCitables(nom, libelles)) {
      if (!sortie.some((champ) => champ.key === natif.key)) sortie.push(natif);
    }
  }
  return sortie;
}

/**
 * LE SAC QUE LIT UN CALCUL : le sac `customFields` de la fiche, plus ses natifs
 * numeriques a la racine, sous leur clef de citation (`socle:basePrice`). C'est
 * le SEUL endroit qui traduit « la racine de la fiche » en lecture de formule :
 * le client (ecriture, brouillon) et le serveur (declencheur) le passent a
 * `recalculer`. Les clefs de `customFields` ne portent jamais de `:`, donc rien
 * ne se confond.
 */
export function sacDeFormule(
  racine: Readonly<Record<string, unknown>> | null | undefined,
  sac: Readonly<Record<string, unknown>> | null | undefined,
): Record<string, unknown> {
  const sortie: Record<string, unknown> = { ...(sac ?? {}) };
  if (racine != null) {
    for (const [clef, valeur] of Object.entries(racine)) {
      if (typeof valeur === "number") sortie[`${PREFIXE_CHAMP_SOCLE}${clef}`] = valeur;
    }
  }
  return sortie;
}

/* -------------------------------------------------------------------------- */
/* Les deux ecritures                                                         */
/* -------------------------------------------------------------------------- */

/*
 * UNE SEULE MISE EN FORME pour les deux ecritures : un espace autour d'un
 * operateur binaire, aucun apres un moins unaire, `; ` entre deux arguments.
 * C'est elle qui rend l'aller-retour exact : `formuleLisible(canoniserFormule(x))`
 * redonne `x` des qu'il etait ecrit ainsi.
 */
function ecrire(jetons: readonly Jeton[], champ: (nom: string) => string, nombre: (texte: string) => string): string {
  let sortie = "";
  let precedent: Jeton | undefined;
  for (const jeton of jetons) {
    switch (jeton.sorte) {
      case "nombre": sortie += nombre(jeton.texte); break;
      case "champ": sortie += champ(jeton.nom); break;
      case "fonction": sortie += jeton.nom; break;
      case "ouvrante": sortie += "("; break;
      case "fermante": sortie += ")"; break;
      case "separateur": sortie += "; "; break;
      case "operateur": {
        const estUnaire = (jeton.operateur === "+" || jeton.operateur === "-")
          && (precedent === undefined || precedent.sorte === "operateur" || precedent.sorte === "ouvrante" || precedent.sorte === "separateur");
        sortie += estUnaire ? jeton.operateur : ` ${jeton.operateur} `;
        break;
      }
    }
    precedent = jeton;
  }
  return sortie;
}

/**
 * LA SAISIE MISE EN FORME STOCKEE : chaque champ par sa clef (`{prix}`), chaque
 * nombre avec un point. Refuse une formule illisible ou un nom qui ne designe
 * aucun champ du jeu (ou plusieurs). La nature des champs cites n'est pas jugee
 * ici : c'est `verifierFormule`.
 */
export function canoniserFormule(saisie: string, champs: readonly ChampDeFormule[]): { formule: string } | { erreur: string } {
  const lu = lire(saisie);
  if ("erreur" in lu) return { erreur: phraseDeFaute(lu) };
  for (const jeton of lu.jetons) {
    if (jeton.sorte !== "champ") continue;
    const resolu = resoudre(jeton.nom, champs);
    if (!("champ" in resolu)) return { erreur: `la formule cite ${phraseDeResolution(jeton.nom, resolu)}` };
  }
  const clef = (nom: string): string => {
    const resolu = resoudre(nom, champs);
    return "champ" in resolu ? `{${resolu.champ.key}}` : nom;
  };
  return { formule: ecrire(lu.jetons, clef, (texte) => texte.replace(",", ".")) };
}

/**
 * LA FORME STOCKEE RENDUE LISIBLE : chaque clef devient son libelle (nu quand
 * c'est un seul mot qui designe ce champ et lui seul, entre crochets sinon),
 * chaque nombre prend la virgule. Un nom qui ne se resout plus reste tel quel :
 * le lecteur voit la faute au lieu d'un libelle invente. Une formule illisible
 * est rendue telle quelle.
 */
export function formuleLisible(formule: string, champs: readonly ChampDeFormule[]): string {
  const lu = lire(formule);
  if ("erreur" in lu) return formule;
  const libelle = (nom: string): string => {
    const resolu = resoudre(nom, champs);
    if (!("champ" in resolu)) return `{${nom}}`;
    const { champ } = resolu;
    /* Un natif sans libelle se lit par sa clef : `{socle:basePrice}`. */
    if (champ.label === undefined && estCitationNative(champ.key)) return `{${champ.key}}`;
    const mot = champ.label ?? champ.key;
    const seRelit = (() => {
      const retour = resoudre(mot, champs);
      return "champ" in retour && retour.champ.key === champ.key;
    })();
    if (MOT_NU.test(mot) && seRelit && fonctionNommee(mot) === undefined) return mot;
    if (!mot.includes("]") && seRelit) return `[${mot}]`;
    return `{${champ.key}}`;
  };
  return ecrire(lu.jetons, libelle, (texte) => texte.replace(".", ","));
}

/* -------------------------------------------------------------------------- */
/* Le controle d'une declaration                                              */
/* -------------------------------------------------------------------------- */

/** Les clefs des champs `calcul` que la formule de ce champ cite (resolus seulement). */
function calculsCites(champ: ChampDeFormule, champsDuJeu: readonly ChampDeFormule[]): string[] {
  if (champ.type !== "calcul" || typeof champ.formule !== "string") return [];
  return champsCites(champ.formule, champsDuJeu).filter((clef) => champsDuJeu.some((autre) => autre.key === clef && autre.type === "calcul"));
}

/** Le chemin qui ramene a `depart` en suivant les calculs cites, ou `null`. */
function boucleDepuis(depart: ChampDeFormule, champsDuJeu: readonly ChampDeFormule[]): string[] | null {
  const vus = new Set<string>();
  const chercher = (clef: string, chemin: string[]): string[] | null => {
    const champ = champsDuJeu.find((candidat) => candidat.key === clef);
    if (champ === undefined) return null;
    for (const suivant of calculsCites(champ, champsDuJeu)) {
      if (suivant === depart.key) return [...chemin, suivant];
      if (vus.has(suivant)) continue;
      vus.add(suivant);
      const trouve = chercher(suivant, [...chemin, suivant]);
      if (trouve !== null) return trouve;
    }
    return null;
  };
  return chercher(depart.key, [depart.key]);
}

/**
 * LES FAUTES DE LA FORMULE D'UN CHAMP, contre les champs de SON jeu, une phrase
 * chacune ; vide, la formule tient. Ce qui est refuse : une formule vide ou
 * illisible, un nom qui ne designe aucun champ du jeu (ou plusieurs), un champ
 * dont la nature n'est pas un nombre (catalogue des types), et une boucle entre
 * calculs (un calcul qui se cite, directement ou par un autre). C'est le meme
 * controle au Studio et a la CI (`appFieldsFileSchema`).
 */
export function verifierFormule(formule: string | undefined, champ: ChampDeFormule, champsDuJeu: readonly ChampDeFormule[]): string[] {
  const de = `la formule de ${nomDuChamp(champ)}`;
  if (typeof formule !== "string" || formule.trim() === "") return [`${de} est vide`];
  const lu = lire(formule);
  if ("erreur" in lu) return [`${de} est illisible : ${phraseDeFaute(lu)}`];
  const fautes: string[] = [];
  const vus = new Set<string>();
  for (const jeton of lu.jetons) {
    if (jeton.sorte !== "champ" || vus.has(jeton.nom)) continue;
    vus.add(jeton.nom);
    const resolu = resoudre(jeton.nom, champsDuJeu);
    if (!("champ" in resolu)) { fautes.push(`${de} cite ${phraseDeResolution(jeton.nom, resolu)}`); continue; }
    const cite = resolu.champ;
    if (cite.key === champ.key) { fautes.push(`${de} se cite elle-même : un calcul ne dépend pas de sa propre valeur`); continue; }
    if (!estNumerique(cite.type)) {
      const genre = ficheDuType(cite.type)?.genre ?? cite.type;
      fautes.push(`${de} cite ${nomDuChamp(cite)} (${genre}) : seul un champ numérique (nombre, note, calcul, oui/non) entre dans une formule`);
    }
  }
  if (fautes.length === 0) {
    const boucle = boucleDepuis({ ...champ, formule }, champsDuJeu.map((autre) => (autre.key === champ.key ? { ...autre, formule } : autre)));
    if (boucle !== null) {
      const noms = boucle.map((clef) => champsDuJeu.find((autre) => autre.key === clef)?.label ?? clef);
      fautes.push(`${de} forme une boucle entre calculs : ${noms.join(" -> ")}`);
    }
  }
  return fautes;
}

/* -------------------------------------------------------------------------- */
/* L'evaluation                                                               */
/* -------------------------------------------------------------------------- */

/**
 * ARRONDIR A `n` DECIMALES, a la moitie loin de zero (comme ARRONDI d'un
 * tableur). Le passage par `toPrecision(15)` efface le bruit binaire avant
 * l'arrondi : sans lui, 1,005 * 100 vaut 100,49999... et 1,005 s'arrondirait a
 * 1,00.
 */
export function arrondir(valeur: number, decimales: number): number {
  const facteur = 10 ** decimales;
  const decale = Number((Math.abs(valeur) * facteur).toPrecision(15));
  const arrondi = (Math.sign(valeur) * Math.round(decale)) / facteur;
  return Object.is(arrondi, -0) ? 0 : arrondi;
}

/** Un nombre lu d'une valeur stockee, ou `null` : nombre fini, ou texte qui n'est QUE un nombre. */
function nombreStocke(valeur: unknown): number | null {
  if (typeof valeur === "number") return Number.isFinite(valeur) ? valeur : null;
  if (typeof valeur === "string") {
    const texte = valeur.trim().replace(",", ".");
    return /^-?\d+(?:\.\d+)?$/.test(texte) ? Number(texte) : null;
  }
  return null;
}

/**
 * LA VALEUR D'UN CHAMP CITE, telle que la formule la compte : un oui/non vaut 1
 * ou 0 (une ancienne valeur `"true"` / `"false"` se lit encore), un nombre vaut
 * sa VALEUR STOCKEE, quel que soit son format : un pourcentage de 65 vaut 65
 * (`{avancement} / 100` pour sa fraction), une duree ses minutes, un montant
 * ses unites de devise. Le format d'un nombre est un affichage
 * (`formatDuNombre`, `types-de-champ.ts`), il ne change pas ce qu'il vaut.
 * Vide ou illisible : `null`, et la formule entiere est vide.
 */
function valeurCitee(champ: ChampDeFormule, valeur: unknown): number | null {
  if (champ.type === "boolean") {
    if (valeur === true || valeur === "true") return 1;
    if (valeur === false || valeur === "false") return 0;
    return null;
  }
  if (!estNumerique(champ.type)) return null;
  return nombreStocke(valeur);
}

const fini = (valeur: number): number | null => (Number.isFinite(valeur) ? valeur : null);

function evaluerNoeud(noeud: NoeudFormule, valeurDe: (nom: string) => number | null): number | null {
  switch (noeud.sorte) {
    case "nombre": return noeud.valeur;
    case "champ": return valeurDe(noeud.nom);
    case "unaire": {
      const operande = evaluerNoeud(noeud.operande, valeurDe);
      return operande === null ? null : noeud.operateur === "-" ? -operande : operande;
    }
    case "binaire": {
      const gauche = evaluerNoeud(noeud.gauche, valeurDe);
      const droite = evaluerNoeud(noeud.droite, valeurDe);
      if (gauche === null || droite === null) return null;
      switch (noeud.operateur) {
        case "+": return fini(gauche + droite);
        case "-": return fini(gauche - droite);
        case "*": return fini(gauche * droite);
        case "/": return droite === 0 ? null : fini(gauche / droite);
        case "^": return fini(gauche ** droite);
      }
    }
    case "fonction": {
      const args: number[] = [];
      for (const argument of noeud.arguments) {
        const valeur = evaluerNoeud(argument, valeurDe);
        if (valeur === null) return null;
        args.push(valeur);
      }
      switch (noeud.nom) {
        case "MIN": return Math.min(...args);
        case "MAX": return Math.max(...args);
        case "ABS": return Math.abs(args[0]!);
        case "ARRONDI": {
          const decimales = Math.trunc(args[1] ?? 0);
          return decimales < -12 || decimales > 12 ? null : fini(arrondir(args[0]!, decimales));
        }
      }
    }
  }
}

/**
 * LA VALEUR D'UNE FORMULE sur une fiche. `lire(clef)` rend la valeur STOCKEE
 * d'un champ du jeu ; la formule en fait un nombre selon le type du champ
 * (`valeurCitee`). `null` quand elle ne se calcule pas : formule illisible, nom
 * qui ne designe aucun champ, champ cite vide, division par zero, resultat
 * infini. Sans `decimales`, le resultat perd son bruit binaire (0,1 + 0,2 vaut
 * 0,3) ; avec, il est arrondi.
 */
export function evaluerFormule(
  formule: string,
  lire: (clef: string) => unknown,
  champsDuJeu: readonly ChampDeFormule[],
  decimales?: number,
): number | null {
  const analyse = analyserFormule(formule);
  if ("erreur" in analyse) return null;
  let introuvable = false;
  const valeurDe = (nom: string): number | null => {
    const resolu = resoudre(nom, champsDuJeu);
    if (!("champ" in resolu)) { introuvable = true; return null; }
    return valeurCitee(resolu.champ, lire(resolu.champ.key));
  };
  const valeur = evaluerNoeud(analyse.arbre, valeurDe);
  if (introuvable || valeur === null || !Number.isFinite(valeur)) return null;
  const propre = decimales === undefined ? Number(valeur.toPrecision(15)) : arrondir(valeur, decimales);
  return Object.is(propre, -0) ? 0 : propre;
}

/**
 * LES DECIMALES D'UN CALCUL : celles qu'il declare ; a defaut, 2 pour un calcul
 * en devise (un montant se compte au centime), aucune sinon.
 */
export function decimalesDuCalcul(champ: ChampDeFormule): number | undefined {
  if (champ.decimales !== undefined) return champ.decimales;
  return champ.devise !== undefined ? 2 : undefined;
}

/**
 * RECALCULER TOUS LES CALCULS D'UNE FICHE, dans l'ordre de leurs dependances (un
 * calcul qui en cite un autre passe apres lui), et rendre SEULEMENT les clefs
 * dont la valeur change. Un patch vide dit « rien a ecrire » : c'est ce qui
 * arrete le declencheur serveur, qui relit la fiche qu'il vient d'ecrire.
 *
 * Un calcul dont la formule est fautive (illisible, champ inconnu, boucle) n'est
 * pas recalcule : sa valeur reste celle d'avant. La declaration qui le porte est
 * refusee au Studio et a la CI ; ici, on n'efface rien sur sa foi.
 */
export function recalculer(
  champsDuJeu: readonly ChampDeFormule[],
  sac: Readonly<Record<string, unknown>> | null | undefined,
): Record<string, number | null> {
  const stocke = (clef: string): unknown => (sac != null && Object.hasOwn(sac, clef) ? sac[clef] : undefined);
  const calculs = champsDuJeu.filter((champ) => champ.type === "calcul" && verifierFormule(champ.formule, champ, champsDuJeu).length === 0);
  const ordre: ChampDeFormule[] = [];
  const places = new Set<string>();
  const placer = (champ: ChampDeFormule): void => {
    if (places.has(champ.key)) return;
    places.add(champ.key);
    for (const clef of calculsCites(champ, champsDuJeu)) {
      const dependance = calculs.find((candidat) => candidat.key === clef);
      if (dependance !== undefined) placer(dependance);
    }
    ordre.push(champ);
  };
  for (const champ of calculs) placer(champ);

  const nouvelles = new Map<string, number | null>();
  const lireValeur = (clef: string): unknown => (nouvelles.has(clef) ? nouvelles.get(clef) : stocke(clef));
  const patch: Record<string, number | null> = {};
  for (const champ of ordre) {
    const valeur = evaluerFormule(champ.formule!, lireValeur, champsDuJeu, decimalesDuCalcul(champ));
    nouvelles.set(champ.key, valeur);
    if ((stocke(champ.key) ?? null) !== valeur) patch[champ.key] = valeur;
  }
  return patch;
}
